import { useEffect, useState, useRef, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { SelectableBox } from '@/components/ui/selectable-box'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  agentsApiClient,
  type AgentWithVoiceSettings,
} from '@/lib/api/agents.client'
import { agentsAdminApiClient } from '@/lib/api/agents-admin.client'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  AGENT_TONE_VALUES,
  VOICE_SETTINGS_DEFAULTS,
  VOICE_OPTIONS,
  AGENT_TEMPLATES,
  type AgentTone,
  type AgentTemplate,
} from '@super/shared-types'
import { Sparkles, MessageSquare, Radio } from 'lucide-react'

const toneOptions = AGENT_TONE_VALUES

const llmOptions = [
  {
    id: 'gpt-4o-mini',
    label: 'GPT-4o Mini',
    description: 'OpenAI - fastest',
  },
  {
    id: 'gpt-4-turbo',
    label: 'GPT-4 Turbo',
    description: 'OpenAI - balanced',
  },
  {
    id: 'gpt-4o',
    label: 'GPT-4o',
    description: 'OpenAI - most capable',
  },
]

const sttOptions = [
  {
    id: 'nova-3',
    label: 'Nova',
    description: 'Deepgram',
  },
]

const voiceOptions = VOICE_OPTIONS

// Get outbound templates only
const outboundTemplates = AGENT_TEMPLATES.filter(
  t => t.direction === 'OUTBOUND',
)

const formSchema = z.object({
  name: z.string().min(1, 'Agent name is required'),
  scenario: z.string().min(1, 'Please select a scenario'),
  prompt: z.string().optional(),
  transferInstructions: z.string().optional(),
  greeting: z.string().optional(),
  tone: z.array(z.enum(AGENT_TONE_VALUES)).min(1, 'Select at least one tone'),
  temperature: z.number().min(0).max(1),
  voice: z.string().min(1, 'Please select a voice'),
  voiceModelId: z.string().min(1, 'Voice model ID is required'),
  voiceModelLLM: z.string().min(1, 'Select an LLM model'),
  voiceModelSTT: z.string().min(1, 'Select an STT model'),
  smsFollowUpText: z.string().optional(),
  isSmsFollowUpEnabled: z.boolean().optional(),
})

type FormValues = z.infer<typeof formSchema>

const getDefaultValues = (template?: AgentTemplate): FormValues => {
  const defaultVoice = VOICE_OPTIONS[0]
  const defaultSTT = sttOptions[0]

  if (template) {
    return {
      name: template.name,
      scenario: template.scenario,
      prompt: template.defaultSettings.prompt || '',
      transferInstructions: template.defaultSettings.transferInstructions || '',
      greeting: template.defaultSettings.greeting || '',
      tone: template.defaultSettings.tone || ['friendly'],
      temperature: template.defaultSettings.temperature || 0.5,
      voice: defaultVoice.label,
      voiceModelId: defaultVoice.id,
      voiceModelLLM: VOICE_SETTINGS_DEFAULTS.voiceModelLLM,
      voiceModelSTT: defaultSTT.id,
      smsFollowUpText: template.defaultSettings.smsFollowUpText || '',
      isSmsFollowUpEnabled:
        template.defaultSettings.isSmsFollowUpEnabled || false,
    }
  }

  return {
    name: '',
    scenario: '',
    prompt: '',
    transferInstructions: '',
    greeting: '',
    tone: ['friendly'],
    temperature: 0.5,
    voice: defaultVoice.label,
    voiceModelId: defaultVoice.id,
    voiceModelLLM: VOICE_SETTINGS_DEFAULTS.voiceModelLLM,
    voiceModelSTT: defaultSTT.id,
    smsFollowUpText: '',
    isSmsFollowUpEnabled: false,
  }
}

const getTemperatureLabel = (value: number): string => {
  if (value === 0) return 'Precise'
  if (value === 0.25) return 'Consistent'
  if (value === 0.5) return 'Balanced'
  if (value === 0.75) return 'Creative'
  if (value === 1) return 'Experimental'
  return `${value}`
}

export function AgentsOutboundPage() {
  useDocumentHead({
    title: 'Outbound Agent',
    description: 'Configure your outbound voice agent',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [agent, setAgent] = useState<AgentWithVoiceSettings | null>(null)
  const smsFollowUpInputRef = useRef<HTMLTextAreaElement>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  })

  const watchedScenario = form.watch('scenario')

  // Get current template based on selected scenario
  const currentTemplate = useMemo(() => {
    return outboundTemplates.find(t => t.scenario === watchedScenario) || null
  }, [watchedScenario])

  useEffect(() => {
    if (currentCompany?.id) {
      loadAgent()
    }
  }, [currentCompany?.id])

  // When template changes, update form with template defaults
  const handleTemplateChange = (scenario: string) => {
    const template = outboundTemplates.find(t => t.scenario === scenario)
    if (template) {
      const defaults = getDefaultValues(template)
      form.reset(defaults)
    }
  }

  const loadAgent = async () => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      // TODO: In the future, load outbound-specific agent
      // For now, we start fresh with template selection
      setLoading(false)
    } catch (error) {
      console.error('Error loading agent:', error)
      toast.error('Failed to load agent settings')
      setLoading(false)
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No current company selected')
      return false
    }

    try {
      setSaving(true)

      const requestData = {
        name: values.name,
        direction: 'OUTBOUND' as const,
        channels: ['CALL' as const],
        metadata: { scenario: values.scenario },
        voiceSettings: {
          tone: values.tone,
          greeting: values.greeting || undefined,
          prompt: values.prompt || undefined,
          transferInstructions: values.transferInstructions || undefined,
          // An emptied field must be sent as null (not undefined) so the
          // update DTO actually clears the stored text instead of leaving
          // the previous custom message in place. Create has no "clear"
          // concept, so omit the field entirely (undefined) there.
          smsFollowUpText:
            values.smsFollowUpText?.trim() || (agent ? null : undefined),
          isSmsFollowUpEnabled: values.isSmsFollowUpEnabled ?? false,
        },
      }

      if (agent) {
        const updated = isSuperAdmin
          ? await agentsAdminApiClient.updateAgentForCompany(
              agent.id,
              requestData,
            )
          : await agentsApiClient.updateAgent(agent.id, requestData)
        setAgent(updated)
        toast.success('Outbound agent updated successfully')
      } else {
        const created = isSuperAdmin
          ? await agentsAdminApiClient.createAgentForCompany({
              ...requestData,
              companyId: currentCompany.id,
            })
          : await agentsApiClient.createAgent(requestData)
        setAgent(created)
        toast.success('Outbound agent created successfully')
      }
      return true
    } catch (error) {
      console.error('Error saving agent:', error)
      toast.error('Failed to save agent settings')
      return false
    } finally {
      setSaving(false)
    }
  }

  const FloatingIslandContent = () => {
    const { hasUnsavedChanges, changedFieldsCount } = useFormChangeTracking()

    const handleSubmit = async (values: FormValues) => {
      const success = await onSubmit(values)
      if (success) {
        form.reset(values)
      }
    }

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="outbound-agent-save"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center mr-4 space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none">
            <span className="font-medium">
              <span className="mr-1">
                {changedFieldsCount} <span>unsaved&nbsp;</span>
                {changedFieldsCount === 1 ? 'change' : 'changes'}
              </span>
            </span>
          </div>

          <div className="flex justify-end items-center space-x-2 pointer-events-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={saving}
              size="sm"
              variant="form"
            >
              <span>
                {saving ? 'Saving...' : agent ? 'Update agent' : 'Create agent'}
              </span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  if (loading) {
    return (
      <div className="p-6 pt-16 space-y-6 mt-6">
        <PageHeader title="Outbound Agent" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <Skeleton className="h-10 w-full max-w-md" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-10 w-full max-w-md" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="pt-16 h-full">
      <FloatingActionIslandProvider>
        <div className="p-6 pt-0 pb-24 space-y-6 mt-6">
          <PageHeader title="Configure your outbound agent" />

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Template Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Start from Template
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="scenario"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Select a scenario</FormLabel>
                        <FormDescription>
                          Each scenario comes with pre-built settings and
                          variables
                        </FormDescription>
                        <Select
                          onValueChange={value => {
                            field.onChange(value)
                            handleTemplateChange(value)
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="max-w-md">
                              <SelectValue placeholder="Select a scenario..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {outboundTemplates.map(template => (
                              <SelectItem
                                key={template.id}
                                value={template.scenario}
                                disabled={template.comingSoon}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{template.name}</span>
                                  {template.comingSoon && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      Coming Soon
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Variables Preview */}
                  {currentTemplate &&
                    currentTemplate.leadVariables.length > 0 && (
                      <div className="mt-6">
                        <Label className="text-sm font-medium">
                          {currentTemplate.scenario.startsWith('EMERGENCY')
                            ? 'Emergency variables'
                            : currentTemplate.scenario.startsWith('LEASING')
                              ? 'Leasing variables'
                              : currentTemplate.scenario.startsWith(
                                    'DELINQUENCY',
                                  )
                                ? 'Delinquency variables'
                                : currentTemplate.scenario.startsWith(
                                      'LEASE_RENEWAL',
                                    )
                                  ? 'Lease renewal variables'
                                  : 'Variables'}
                        </Label>
                        <div className="mt-2 p-4 bg-muted rounded-md font-mono text-sm">
                          {currentTemplate.leadVariables.map(variable => (
                            <div
                              key={variable.name}
                              className="text-muted-foreground"
                            >
                              {`{${variable.name}}`}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Agent Configuration - Only show when template is selected */}
              {watchedScenario && (
                <>
                  {/* Basic Settings */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Agent Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 max-w-156">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="max-w-96">
                            <FormLabel>Agent name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter agent name"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="greeting"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Agent greeting</FormLabel>
                            <FormDescription>
                              The opening message when the agent calls
                            </FormDescription>
                            <FormControl>
                              <Textarea
                                placeholder="Hi, this is {agentName} with {companyName}..."
                                className="min-h-[100px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="prompt"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Prompt</FormLabel>
                            <FormDescription>
                              Detailed instructions for how the agent should
                              conduct conversations
                            </FormDescription>
                            <FormControl>
                              <Textarea
                                placeholder="You are a leasing AI that helps call prospective residents..."
                                className="min-h-[200px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="transferInstructions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Transfer instructions</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Allow call transfers during business hours only..."
                                className="min-h-[100px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* SMS Follow-up */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        SMS Follow-up
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 max-w-156">
                      <FormField
                        control={form.control}
                        name="isSmsFollowUpEnabled"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-4">
                              <FormLabel className="mb-0">
                                Send SMS after every outbound call
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value ?? false}
                                  onCheckedChange={checked => {
                                    field.onChange(checked)
                                    if (checked) {
                                      requestAnimationFrame(() => {
                                        smsFollowUpInputRef.current?.focus()
                                      })
                                    }
                                  }}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="smsFollowUpText"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>SMS message</FormLabel>
                            <FormControl>
                              <Textarea
                                {...(() => {
                                  const { ref, ...fieldProps } = field
                                  return {
                                    ...fieldProps,
                                    ref: (e: HTMLTextAreaElement | null) => {
                                      ref(e)
                                      smsFollowUpInputRef.current = e
                                    },
                                  }
                                })()}
                                placeholder="Thanks for your interest in {property}! Here's the link to schedule a showing: {listingURL}"
                                className="min-h-[100px]"
                                disabled={!form.watch('isSmsFollowUpEnabled')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Voice & Model Settings */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Radio className="w-5 h-5" />
                        Voice Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 max-w-156">
                      <FormField
                        control={form.control}
                        name="tone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tone</FormLabel>
                            <FormControl>
                              <div className="flex flex-wrap gap-2">
                                {toneOptions.map(tone => (
                                  <SelectableBox
                                    key={tone}
                                    id={tone}
                                    selected={field.value.includes(tone)}
                                    onSelect={id => {
                                      const typedId = id as AgentTone
                                      const newValue = field.value.includes(
                                        typedId,
                                      )
                                        ? field.value.filter(t => t !== typedId)
                                        : [...field.value, typedId]
                                      field.onChange(newValue)
                                    }}
                                  >
                                    <span className="capitalize">{tone}</span>
                                  </SelectableBox>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="temperature"
                        render={({ field }) => (
                          <FormItem className="max-w-96">
                            <FormLabel>Temperature</FormLabel>
                            <FormControl>
                              <Slider
                                min={0}
                                max={1}
                                step={0.25}
                                value={[field.value]}
                                onValueChange={value =>
                                  field.onChange(value[0])
                                }
                                getTooltipLabel={getTemperatureLabel}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="voiceModelId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Voice</FormLabel>
                            <FormControl>
                              <div className="flex flex-wrap gap-2">
                                {voiceOptions.map(option => (
                                  <SelectableBox
                                    key={option.id}
                                    id={option.id}
                                    selected={field.value === option.id}
                                    onSelect={id => {
                                      field.onChange(id)
                                      const selectedOption = voiceOptions.find(
                                        o => o.id === id,
                                      )
                                      form.setValue(
                                        'voice',
                                        selectedOption?.label || '',
                                      )
                                    }}
                                  >
                                    <div className="space-y-0.5">
                                      <div className="font-medium">
                                        {option.label}
                                      </div>
                                      <div
                                        className={cn(
                                          'text-xs',
                                          field.value === option.id
                                            ? 'text-white/80'
                                            : 'text-[#757575]',
                                        )}
                                      >
                                        {option.description}
                                      </div>
                                    </div>
                                  </SelectableBox>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="voiceModelLLM"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Language Model (LLM)</FormLabel>
                            <FormControl>
                              <div className="flex flex-wrap gap-2">
                                {llmOptions.map(option => (
                                  <SelectableBox
                                    key={option.id}
                                    id={option.id}
                                    selected={field.value === option.id}
                                    onSelect={id => field.onChange(id)}
                                  >
                                    <div className="space-y-0.5">
                                      <div className="font-medium">
                                        {option.label}
                                      </div>
                                      <div
                                        className={cn(
                                          'text-xs',
                                          field.value === option.id
                                            ? 'text-white/80'
                                            : 'text-[#757575]',
                                        )}
                                      >
                                        {option.description}
                                      </div>
                                    </div>
                                  </SelectableBox>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="voiceModelSTT"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Speech-to-Text Model (STT)</FormLabel>
                            <FormControl>
                              <div className="flex flex-wrap gap-2">
                                {sttOptions.map(option => (
                                  <SelectableBox
                                    key={option.id}
                                    id={option.id}
                                    selected={field.value === option.id}
                                    onSelect={id => field.onChange(id)}
                                  >
                                    <div className="space-y-0.5">
                                      <div className="font-medium">
                                        {option.label}
                                      </div>
                                      <div
                                        className={cn(
                                          'text-xs',
                                          field.value === option.id
                                            ? 'text-white/80'
                                            : 'text-[#757575]',
                                        )}
                                      >
                                        {option.description}
                                      </div>
                                    </div>
                                  </SelectableBox>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </>
              )}

              <FloatingIslandContent />
            </form>
          </Form>
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
